import React, { useState } from 'react';
import { Shield, CheckCircle, AlertTriangle, ExternalLink, ChevronDown, ChevronUp, Bell, Search, Calendar, Globe } from 'lucide-react';

const BrandMonitoringPanel = () => {
  const [expandedSection, setExpandedSection] = useState('google-alerts');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const googleAlertTerms = [
    '"MyBodyToday"',
    '"My Body Today" app',
    '"MyBodyToday" trademark',
    '"Track small changes before they become big ones"',
    'mybodytoday site:*.com',
    'mybodytoday1.com competitor',
  ];

  const monthlyTasks = [
    { task: 'Check Google Alerts inbox for new mentions', priority: 'high', icon: Bell },
    { task: 'Search App Store (iOS) for "MyBodyToday" or similar names', priority: 'high', icon: Search },
    { task: 'Search Google Play Store for copycat apps', priority: 'high', icon: Search },
    { task: 'Search UK IPO trademark database for new filings', priority: 'high', icon: Shield },
    { task: 'Search EUIPO trademark database for EU filings', priority: 'medium', icon: Shield },
    { task: 'Check domain registrations: mybodytoday.co.uk, .net, .org, .io', priority: 'medium', icon: Globe },
    { task: 'Search social media for @MyBodyToday handles (Instagram, TikTok, X)', priority: 'medium', icon: Search },
    { task: 'Review trademark application status on UK IPO portal', priority: 'high', icon: Calendar },
    { task: 'Check for copycat landing pages via Google image search of logo', priority: 'low', icon: Search },
    { task: 'Document any potential infringements found this month', priority: 'medium', icon: CheckCircle },
  ];

  const competitors = [
    { name: 'Symple Symptom Tracker', platform: 'iOS/Android', notes: 'Monitor for brand name similarity' },
    { name: 'Bearable', platform: 'iOS/Android', notes: 'Health tracking competitor — watch for feature copying' },
    { name: 'Cara Care', platform: 'iOS/Android', notes: 'Symptom journal — monitor branding' },
    { name: 'Flaredown', platform: 'Web/iOS', notes: 'Chronic condition tracker — watch for tagline similarity' },
    { name: 'Manage My Pain', platform: 'iOS/Android', notes: 'Pain tracking — monitor for design copying' },
  ];

  const priorityColor = (p) => {
    if (p === 'high') return 'bg-red-100 text-red-700 border-red-200';
    if (p === 'medium') return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-purple-50">
        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-foreground">Brand Monitoring &amp; Trademark Protection</h2>
          <p className="text-xs text-muted-foreground">MyBodyToday&#8482; — Pending Trademark Monitoring Guide</p>
        </div>
      </div>
      <div className="p-6 space-y-4">

        {/* Status Banner */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Trademark Application Pending</p>
            <p className="text-xs text-amber-800 mt-1">
              "MyBodyToday"&#8482;, logo&#8482;, and tagline "Track small changes before they become big ones"&#8482; are pending trademarks.
              Use &#8482; symbol on all brand materials. Switch to ® once registration is confirmed.
            </p>
          </div>
        </div>

        {/* Google Alerts Setup */}
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('google-alerts')}
            className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Google Alerts Setup Instructions</span>
            </div>
            {expandedSection === 'google-alerts' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {expandedSection === 'google-alerts' && (
            <div className="px-4 py-4 space-y-3">
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-blue-900">Step 1: Go to Google Alerts</p>
                  <a
                    href="https://www.google.com/alerts"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-700 underline hover:text-blue-900"
                  >
                    https://www.google.com/alerts
                  </a>
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-medium">Step 2: Create alerts for each of these search terms:</p>
              <div className="space-y-2">
                {googleAlertTerms?.map((term, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-muted/30 rounded border border-border">
                    <Search className="w-3 h-3 text-primary flex-shrink-0" />
                    <code className="text-xs text-foreground font-mono">{term}</code>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs font-semibold text-green-900 mb-1">Recommended Alert Settings:</p>
                <ul className="text-xs text-green-800 space-y-1 list-disc list-inside">
                  <li>How often: <strong>As-it-happens</strong> (for brand name) or <strong>Once a day</strong></li>
                  <li>Sources: <strong>Automatic</strong></li>
                  <li>Language: <strong>English</strong></li>
                  <li>Region: <strong>Any Region</strong></li>
                  <li>How many: <strong>All results</strong></li>
                  <li>Deliver to: your legal/brand email</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Monthly Monitoring Tasks */}
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('monthly-tasks')}
            className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Monthly Monitoring Checklist</span>
            </div>
            {expandedSection === 'monthly-tasks' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {expandedSection === 'monthly-tasks' && (
            <div className="px-4 py-4 space-y-2">
              {monthlyTasks?.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 border border-border rounded-lg bg-white hover:bg-muted/20 transition-colors">
                  <item.icon className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground leading-relaxed">{item?.task}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${priorityColor(item?.priority)}`}>
                    {item?.priority}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Competitor Tracking */}
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleSection('competitors')}
            className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Competitor Tracking</span>
            </div>
            {expandedSection === 'competitors' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {expandedSection === 'competitors' && (
            <div className="px-4 py-4 space-y-2">
              <p className="text-xs text-muted-foreground mb-3">Monitor these known competitors monthly for brand name or design copying:</p>
              {competitors?.map((comp, i) => (
                <div key={i} className="p-3 border border-border rounded-lg bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold text-foreground">{comp?.name}</p>
                      <p className="text-xs text-muted-foreground">{comp?.platform}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 italic">{comp?.notes}</p>
                </div>
              ))}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mt-3">
                <p className="text-xs font-semibold text-blue-900 mb-1">If you find a potential infringement:</p>
                <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Screenshot and date-stamp the evidence</li>
                  <li>Document the URL, app store listing, or social handle</li>
                  <li>Email <strong>legal@mybodytoday.com</strong> with evidence</li>
                  <li>Do not contact the infringing party directly</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href="https://www.gov.uk/search-for-trademark"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground">UK IPO Trademark Search</p>
              <p className="text-xs text-muted-foreground">Check application status</p>
            </div>
          </a>
          <a
            href="https://euipo.europa.eu/eSearch/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground">EUIPO Trademark Search</p>
              <p className="text-xs text-muted-foreground">EU trademark database</p>
            </div>
          </a>
          <a
            href="https://www.google.com/alerts"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground">Google Alerts</p>
              <p className="text-xs text-muted-foreground">Set up brand monitoring</p>
            </div>
          </a>
          <a
            href="https://tmsearch.uspto.gov/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <ExternalLink className="w-4 h-4 text-primary flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground">USPTO Trademark Search</p>
              <p className="text-xs text-muted-foreground">US trademark database</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default BrandMonitoringPanel;
